import { router } from "@inertiajs/react";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { VerificationStatusBadge } from "@/components/verification-status-badge";

interface VerificationCardProps {
	verified: boolean;
	verifiedAt: string | null;
	verificationExpiresAt: string | null;
	verifyUrl: string;
	unverifyUrl: string;
}

export function VerificationCard({
	verified,
	verifiedAt,
	verificationExpiresAt,
	verifyUrl,
	unverifyUrl,
}: VerificationCardProps) {
	const [verifyDate, setVerifyDate] = useState<string | null>(
		new Date().toISOString().split("T")[0],
	);

	const today = new Date();
	today.setHours(0, 0, 0, 0);

	const oneYearAgo = new Date(today);
	oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
	oneYearAgo.setDate(oneYearAgo.getDate() + 1); // Exclude exactly 1 year ago (would be expired)

	const handleVerify = () => {
		router.post(
			verifyUrl,
			{ verified_at: verifyDate },
			{
				preserveScroll: true,
				onSuccess: () => {
					toast.success("Datum identifikace bylo uloženo.");
				},
				onError: () => {
					toast.error("Nepodařilo se uložit datum identifikace.");
				},
			},
		);
	};

	const handleUnverify = () => {
		router.post(
			unverifyUrl,
			{},
			{
				preserveScroll: true,
				onSuccess: () => {
					toast.success("Identifikace byla úspěšně smazána.");
				},
				onError: () => {
					toast.error("Nepodařilo se smazat identifikaci.");
				},
			},
		);
	};

	if (verified && verifiedAt) {
		return (
			<div className="rounded-lg border p-4">
				<div className="flex flex-col items-center justify-between gap-3">
					<div className="w-full">
						<div className="flex items-center justify-between gap-2">
							<span className="font-medium text-sm">Identifikace</span>
							<VerificationStatusBadge
								entries={[
									{
										verified: true,
										verification_expires_at: verificationExpiresAt,
									},
								]}
							/>
						</div>
						<div className="mt-3">
							<table className="w-full text-muted-foreground text-sm">
								<tbody>
									<tr>
										<td className="py-1 pr-4">Identifikováno dne</td>
										<td className="py-1 text-right font-medium">
											{new Date(verifiedAt).toLocaleDateString("cs-CZ")}
										</td>
									</tr>
									{verificationExpiresAt && (
										<tr>
											<td className="py-1 pr-4">Platnost do</td>
											<td className="py-1 text-right font-medium">
												{new Date(verificationExpiresAt).toLocaleDateString(
													"cs-CZ",
												)}
											</td>
										</tr>
									)}
								</tbody>
							</table>
						</div>
					</div>
					<Button
						type="button"
						variant="outline"
						size="sm"
						className="w-full"
						onClick={handleUnverify}
					>
						Smazat
					</Button>
				</div>
			</div>
		);
	}

	return (
		<div className="rounded-lg border p-4">
			<div className="flex items-center justify-between gap-2">
				<span className="font-medium text-sm">Identifikace</span>
				<VerificationStatusBadge entries={[{ verified: false }]} />
			</div>
			<div className="mt-3 flex flex-col gap-3">
				<div className="flex items-center justify-between gap-4">
					<label className="line-clamp-1 text-muted-foreground text-sm">
						Datum identifikace
					</label>
					<DatePicker
						value={verifyDate}
						onChange={setVerifyDate}
						placeholder="Vyberte datum"
						fromDate={oneYearAgo}
						toDate={today}
						className="w-auto shrink"
					/>
				</div>

				<Button type="button" onClick={handleVerify} disabled={!verifyDate}>
					Uložit datum identifikace
				</Button>
			</div>
		</div>
	);
}
